import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import TrustWealthLogo from '../TrustWealthLogo';
import {
  HomeIcon,
  ArrowsRightLeftIcon,
  CreditCardIcon,
  BanknotesIcon,
  Cog6ToothIcon,
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  Bars3Icon,
  XMarkIcon,
  BellIcon,
  QuestionMarkCircleIcon,
  ArrowRightOnRectangleIcon,
  CircleStackIcon,
  ListBulletIcon,
  DocumentTextIcon,
  DevicePhoneMobileIcon,
  UserPlusIcon,
  SparklesIcon,
  SunIcon,
  MoonIcon,
  EnvelopeIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';

const scrollToSection = (sectionId) => {
  const element = document.getElementById(sectionId);
  if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const scrollToQuickAction = (navigate, actionId) => {
  // Use React Router so QuickActions' useLocation() sees the hash and opens the right panel
  navigate(`/dashboard#quick-actions-${actionId}`, { replace: true });
};

export default function DashboardLayout({ children }) {
  const { currentUser, userRole, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = userRole === 'admin';
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const profileRef = useRef(null);
  const notificationsRef = useRef(null);

  const handleNavigation = (path, sectionId) => {
    if (window.location.pathname !== '/dashboard') {
      navigate('/dashboard');
      setTimeout(() => scrollToSection(sectionId), 100);
    } else {
      scrollToSection(sectionId);
    }
  };

  const memberNavGroups = [
    {
      label: null,
      items: [
        { name: 'Dashboard', action: () => navigate('/dashboard'), path: '/dashboard', icon: HomeIcon },
      ],
    },
    {
      label: 'Banking',
      items: [
        { name: 'My account', action: () => handleNavigation('/dashboard', 'accounts-section'), path: null, icon: CircleStackIcon },
        { name: 'Transfers', action: () => handleNavigation('/dashboard', 'transfer-section'), path: null, icon: ArrowsRightLeftIcon },
        { name: 'Transactions', action: () => handleNavigation('/dashboard', 'transactions-section'), path: null, icon: ListBulletIcon },
      ],
    },
    {
      label: 'Services',
      items: [
        { name: 'Statements', action: () => scrollToQuickAction(navigate, 'statements'), path: null, icon: DocumentTextIcon },
        { name: 'Check deposit', action: () => scrollToQuickAction(navigate, 'deposit'), path: null, icon: DevicePhoneMobileIcon },
        { name: 'Cards', action: () => scrollToQuickAction(navigate, 'cards'), path: null, icon: CreditCardIcon },
        { name: 'Bills', action: () => scrollToQuickAction(navigate, 'billpay'), path: null, icon: BanknotesIcon },
        { name: 'Pay a contact', action: () => scrollToQuickAction(navigate, 'paycontact'), path: null, icon: UserPlusIcon },
        { name: 'Cashback', action: () => scrollToQuickAction(navigate, 'rewards'), path: null, icon: SparklesIcon },
      ],
    },
  ];

  const adminNav = [
    { name: 'Admin Dashboard', action: () => navigate('/admin'), path: '/admin', icon: Cog6ToothIcon },
    { name: 'Reset Transfers', action: () => navigate('/admin/reset-transfers'), path: '/admin/reset-transfers', icon: ArrowPathIcon },
  ];

  const navigation = isAdmin ? adminNav : null;
  const memberNavGroupsForRender = isAdmin ? null : memberNavGroups;

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  useEffect(() => {
    if (!showNotifications) return;
    const handleClickOutside = (e) => {
      if (notificationsRef.current && !notificationsRef.current.contains(e.target)) setShowNotifications(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showNotifications]);

  useEffect(() => {
    if (!isProfileOpen) return;
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setIsProfileOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isProfileOpen]);

  const isActive = (item) => {
    if (!item.path) return false;
    if (item.path === '/admin') return location.pathname === '/admin';
    return location.pathname === item.path;
  };

  const notifications = [
    { id: 1, type: 'success', message: 'A new login session started', time: 'Now' },
    { id: 2, type: 'info', message: 'New statement is available for viewing', time: '1 day ago' },
    { id: 3, type: 'warning', message: 'Please verify your email address', time: '2 days ago' },
  ];

  const NavLinkButton = ({ item, onItemClick }) => {
    const active = isActive(item);
    const Icon = item.icon;
    return (
      <button
        type="button"
        onClick={() => { item.action(); onItemClick?.(); }}
        className={`group flex items-center w-full px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${
          isAdmin
            ? active ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50 hover:text-amber-700 dark:hover:text-amber-300'
            : active ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50 hover:text-primary-600 dark:hover:text-primary-400'
        }`}
      >
        <Icon className={`mr-3 h-5 w-5 shrink-0 ${active ? (isAdmin ? 'text-amber-600 dark:text-amber-400' : 'text-primary-600 dark:text-primary-400') : 'text-slate-400 dark:text-slate-500 group-hover:text-slate-500 dark:group-hover:text-slate-300'}`} />
        {item.name}
      </button>
    );
  };

  const NavLinks = ({ onItemClick }) => (
    <>
      {isAdmin ? (
        navigation.map((item) => <NavLinkButton key={item.name} item={item} onItemClick={onItemClick} />)
      ) : (
        memberNavGroupsForRender?.map((group, groupIdx) => (
          <div key={group.label || groupIdx} className={group.label ? 'mt-6 first:mt-0' : ''}>
            {group.label && (
              <p className="px-3 mb-1.5 text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                {group.label}
              </p>
            )}
            <div className="space-y-0.5">
              {group.items.map((item) => (
                <NavLinkButton key={item.name} item={item} onItemClick={onItemClick} />
              ))}
            </div>
          </div>
        ))
      )}
    </>
  );

  return (
    <div className="min-h-screen bg-slate-50/80 dark:bg-slate-900">
      {/* Mobile menu backdrop */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-slate-800/50 dark:bg-slate-950/70 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden
        />
      )}

      {/* Sidebar - desktop (lg+) */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:flex lg:w-64 lg:flex-col lg:z-30">
        <div className="flex flex-col flex-grow bg-white dark:bg-slate-800 border-r border-slate-200/80 dark:border-slate-700 shadow-sm">
          <div className="flex items-center h-16 px-4 border-b border-slate-100 dark:border-slate-700 shrink-0">
            <TrustWealthLogo />
          </div>
          <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
            <NavLinks />
          </nav>
          <div className="p-3 border-t border-slate-100 dark:border-slate-700 space-y-0.5">
            <button
              type="button"
              onClick={toggleTheme}
              className="flex items-center w-full px-3 py-2.5 text-sm font-medium text-slate-600 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? (
                <SunIcon className="mr-3 h-5 w-5 text-amber-400" />
              ) : (
                <MoonIcon className="mr-3 h-5 w-5 text-slate-400" />
              )}
              {isDark ? 'Light mode' : 'Dark mode'}
            </button>
            <button
              type="button"
              onClick={() => setShowHelpModal(true)}
              className="flex items-center w-full px-3 py-2.5 text-sm font-medium text-slate-600 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 hover:text-primary-600 dark:hover:text-primary-400 transition-colors text-left"
            >
              <QuestionMarkCircleIcon className="mr-3 h-5 w-5 text-slate-400 dark:text-slate-500 shrink-0" />
              Help
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center w-full px-3 py-2.5 text-sm font-medium text-slate-600 dark:text-slate-300 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 transition-colors"
            >
              <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5 text-slate-400 dark:text-slate-500" />
              Sign out
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile slide-out sidebar */}
      <div
        className={`fixed inset-y-0 left-0 w-64 bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700 shadow-xl transform transition-transform duration-300 ease-out z-50 lg:hidden ${
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between h-16 px-4 border-b border-slate-100 dark:border-slate-700">
            <TrustWealthLogo />
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="p-2 text-slate-500 dark:text-slate-400 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
              aria-label="Close menu"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
          <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
            <NavLinks onItemClick={() => setIsMobileMenuOpen(false)} />
          </nav>
          <div className="p-3 border-t border-slate-100 dark:border-slate-700 space-y-0.5">
            <button
              type="button"
              onClick={toggleTheme}
              className="flex items-center w-full px-3 py-2.5 text-sm font-medium text-slate-600 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50"
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? <SunIcon className="mr-3 h-5 w-5 text-amber-400" /> : <MoonIcon className="mr-3 h-5 w-5 text-slate-400" />}
              {isDark ? 'Light mode' : 'Dark mode'}
            </button>
            <button
              type="button"
              onClick={() => { setShowHelpModal(true); setIsMobileMenuOpen(false); }}
              className="flex items-center w-full px-3 py-2.5 text-sm font-medium text-slate-600 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 hover:text-primary-600 dark:hover:text-primary-400 transition-colors text-left"
            >
              <QuestionMarkCircleIcon className="mr-3 h-5 w-5 text-slate-400 dark:text-slate-500 shrink-0" />
              Help
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center w-full px-3 py-2.5 text-sm font-medium text-slate-600 dark:text-slate-300 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400"
            >
              <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5 text-slate-400 dark:text-slate-500" />
              Sign out
            </button>
          </div>
        </div>
      </div>

      {/* Main area: top bar + content */}
      <div className="lg:pl-64">
        {/* Top bar - slim on all breakpoints when using this layout */}
        <header className="sticky top-0 z-20 flex items-center h-14 sm:h-16 px-4 sm:px-6 bg-white dark:bg-slate-800 border-b border-slate-100 dark:border-slate-700 shadow-sm">
          <button
            type="button"
            onClick={() => setIsMobileMenuOpen(true)}
            className="p-2 text-slate-500 dark:text-slate-400 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 lg:hidden"
            aria-label="Open menu"
          >
            <Bars3Icon className="w-6 h-6" />
          </button>
          <div className="flex-1 min-w-0" />
          <div className="flex items-center gap-1 sm:gap-2">
            <button
              type="button"
              onClick={() => setShowHelpModal(true)}
              className="p-2 text-slate-500 dark:text-slate-400 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-slate-700 dark:hover:text-slate-200 lg:hidden"
              aria-label="Help"
            >
              <QuestionMarkCircleIcon className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
            <div className="relative" ref={notificationsRef}>
              <button
                type="button"
                onClick={() => setShowNotifications(!showNotifications)}
                className="p-2 text-slate-500 dark:text-slate-400 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-slate-700 dark:hover:text-slate-200"
                aria-label="Notifications"
              >
                <BellIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full" aria-hidden />
              </button>
              {showNotifications && (
                <div className="absolute right-0 mt-2 w-72 max-w-[calc(100vw-2rem)] rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-lg py-2 z-50">
                  <div className="px-4 py-2 border-b border-slate-100 dark:border-slate-700">
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Notifications</h3>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {notifications.map((n) => (
                      <div key={n.id} className="px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-700/50">
                        <p className="text-sm text-slate-900 dark:text-slate-200">{n.message}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{n.time}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="relative" ref={profileRef}>
              <button
                type="button"
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                className="flex items-center gap-2 p-1.5 pr-2 rounded-lg text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white text-sm font-medium shrink-0">
                  {currentUser?.email?.[0].toUpperCase() || 'U'}
                </div>
                <span className="hidden sm:block text-sm font-medium truncate max-w-[120px] text-slate-900 dark:text-slate-100">
                  {currentUser?.email?.split('@')[0]}
                </span>
                <svg className="hidden sm:block w-4 h-4 text-slate-400 dark:text-slate-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {isProfileOpen && (
                <div className="absolute right-0 mt-2 w-56 rounded-xl bg-white dark:bg-slate-800 py-2 shadow-lg border border-slate-200 dark:border-slate-600 z-50">
                  <div className="px-4 py-2 border-b border-slate-100 dark:border-slate-700">
                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{currentUser?.email}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {isAdmin ? <span className="text-amber-600 dark:text-amber-400 font-medium">Admin</span> : 'Personal Banking'}
                    </p>
                  </div>
                  {isAdmin && (
                    <>
                      <button
                        type="button"
                        onClick={() => { navigate('/admin'); setIsProfileOpen(false); }}
                        className="w-full flex items-center px-4 py-2 text-sm text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 text-left"
                      >
                        Admin Dashboard
                      </button>
                      <button
                        type="button"
                        onClick={() => { navigate('/admin/reset-transfers'); setIsProfileOpen(false); }}
                        className="w-full flex items-center px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50 text-left"
                      >
                        Reset Transfers
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    onClick={() => { handleLogout(); setIsProfileOpen(false); }}
                    className="w-full flex items-center px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 text-left"
                  >
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 text-slate-900 dark:text-slate-100">
          {children}
        </main>
      </div>

      {/* Help modal */}
      {showHelpModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" aria-modal="true" role="dialog" aria-labelledby="help-modal-title">
          <div className="absolute inset-0 bg-slate-900/60 dark:bg-slate-950/70 backdrop-blur-sm" onClick={() => setShowHelpModal(false)} aria-hidden />
          <div className="relative w-full max-w-md rounded-2xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 dark:border-slate-700">
              <h2 id="help-modal-title" className="text-lg font-semibold text-slate-900 dark:text-slate-100">Help &amp; support</h2>
              <button
                type="button"
                onClick={() => setShowHelpModal(false)}
                className="p-2 text-slate-500 dark:text-slate-400 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-700 dark:hover:text-slate-200"
                aria-label="Close"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-4">
              <p className="text-sm text-slate-600 dark:text-slate-300">
                Get in touch with our team for account help, transfers, or general enquiries.
              </p>
              <div className="space-y-3">
                <a
                  href="mailto:support@willowvalecu.com"
                  className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 transition-colors"
                >
                  <EnvelopeIcon className="w-5 h-5 text-primary-600 dark:text-primary-400 shrink-0" />
                  <div className="text-left">
                    <p className="font-medium text-sm">Email support</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">support@willowvalecu.com</p>
                  </div>
                </a>
                <div className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50">
                  <PhoneIcon className="w-5 h-5 text-primary-600 dark:text-primary-400 shrink-0" />
                  <div className="text-left">
                    <p className="font-medium text-sm">Phone support</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">(808) 860-2349 — 24/7</p>
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => { navigate('/contact'); setShowHelpModal(false); }}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium transition-colors"
              >
                Full contact page
                <ArrowTopRightOnSquareIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
